// ABOUTME: Entry point for the relation-portal service desk page.
// ABOUTME: Renders a dashboard top row + workscreen view inside TabView3, mirroring the warehouse env layout.

import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OverflowRow from "../../../components/theme/OverflowRow";
import TabView3 from "../../../components/theme/TabView3/TabView3";
import Text from "../../../components/theme/text/Text";
import { useTopbar } from "../../../contexts/TopbarProvider";
import { useGetRelationSupportTicketDashboardQuery } from "../../../newapi/warehouse/supportTicketApiSlice";
import { tokens } from "../../../theme";
import RelationServiceDeskWorkScreen from "./views/WorkScreen";
import RelationHistory from "./views/History";
import { relationServiceConfig } from "./relationServiceConfig";

const TopRow = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { data: dashboardData, isLoading, isFetching } = useGetRelationSupportTicketDashboardQuery(
        undefined,
        { pollingInterval: 180000 }
    );

    const metrics = dashboardData?.data || {};
    const showValue = (v) => (!isLoading && !isFetching ? v ?? 0 : '-');

    return (
        <OverflowRow>
            <Box
                sx={{
                    flex: 2,
                    position: "relative",
                    borderRadius: 4,
                    display: "flex",
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                        p: 1,
                        pb: 1.5,
                        px: 1.5,
                        borderRadius: 5,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            p: 1,
                            px: 1.5,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: 'column' }}>
                            <Text>{t("tickets")}</Text>
                            <Text light>{t("overview")}</Text>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            mt: 1,
                            display: "flex",
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                borderRadius: 4,
                                px: 2,
                                py: 1.5,
                                background: colors.red[100],
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <Text variant="red" semibold>{t("open")}</Text>
                            <Typography
                                sx={{
                                    lineHeight: "17px",
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: 17,
                                    fontWeight: "700",
                                    color: colors.txt["primary"],
                                    mt: 'auto',
                                }}
                            >
                                {showValue(metrics.openCount)}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                borderRadius: 4,
                                px: 2,
                                py: 1.5,
                                background: colors.green[100],
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <Text variant="green" semibold>{t("closed")}</Text>
                            <Typography
                                sx={{
                                    lineHeight: "17px",
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: 17,
                                    fontWeight: "700",
                                    color: colors.txt["primary"],
                                    mt: 'auto',
                                }}
                            >
                                {showValue(metrics.closedCount)}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                borderRadius: 4,
                                px: 2,
                                py: 1.5,
                                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <Text light noBreak semibold>{t("total")}</Text>
                            <Typography
                                sx={{
                                    lineHeight: "17px",
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: 17,
                                    fontWeight: "700",
                                    color: colors.txt["primary"],
                                    mt: 'auto',
                                }}
                            >
                                {showValue(metrics.totalCount)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </OverflowRow>
    );
};

const RelationServiceDesk = () => {
    const { t, ready, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { showExtraTitle, clearExtraTitle } = useTopbar();
    const [config] = useState(relationServiceConfig());

    useEffect(() => {
        showExtraTitle(t("servicedesk"));

        return () => {
            clearExtraTitle();
        };
    }, [showExtraTitle, clearExtraTitle, t, i18n.language]);

    return (
        <Box sx={{ background: "", width: 1, paddingTop: 9 }}>
            {/* <TopRow /> */}

            {ready ?
                <TabView3
                    tabConfig={config}
                    sx={{
                        minHeight: "calc(100%)",
                        background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        position: "relative",
                        zIndex: 2,
                        display: "flex",
                        flexDirection: "column",

                        "&:before": {
                            content: "''",
                            display: "block",
                            width: "32px",
                            height: "32px",
                            position: "absolute",
                            left: 0,
                            top: 0,
                            zIndex: 1,
                            backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                            top: "40px",
                            height: "48px",
                            width: "24px",
                            borderTopLeftRadius: "24px",
                            boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                            transition: "250ms",
                        },

                        "&:after": {
                            content: "''",
                            display: "block",
                            width: "32px",
                            height: "32px",
                            position: "absolute",
                            top: 0,
                            zIndex: 1,
                            backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                            top: "40px",
                            right: 0,
                            height: "48px",
                            width: "24px",
                            borderTopLeftRadius: "24px",
                            boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                            transform: "scaleX(-1)",
                            transition: "250ms",
                        },

                        "@media screen and (max-width: 47.9375em)": {
                            "&:after, &:before": {
                                top: "-0px",
                            },
                        },
                    }}
                >
                    <>
                        <RelationServiceDeskWorkScreen config={config.workscreen} />
                        <RelationHistory config={config.history} />
                    </>
                </TabView3>
                : null}
        </Box>
    );
};

export default RelationServiceDesk;
